import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { MessageSquare, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface TaskCommentsProps {
  taskId: string;
  projectTaskId?: string;
  organizationId: string;
  userRole?: string | null;
  isSharedView?: boolean;
}

export const TaskComments = ({ 
  taskId, 
  projectTaskId, 
  organizationId, 
  userRole,
  isSharedView = false 
}: TaskCommentsProps) => {
  const [newComment, setNewComment] = useState("");
  const [guestName, setGuestName] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Check if user is authenticated - force guest mode in shared view
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      // If in shared view, always treat as guest
      if (isSharedView) {
        setIsAuthenticated(false);
        return;
      }
      
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session?.user);
    };
    checkAuth();
  }, [isSharedView]);

  const { data: comments, isLoading } = useQuery({
    queryKey: ["task-comments", taskId, projectTaskId],
    queryFn: async () => {
      console.log("Fetching comments for:", { taskId, projectTaskId, organizationId });
      
      let query = supabase
        .from("task_comments")
        .select(`
          id,
          content,
          created_at,
          created_by,
          created_by_guest,
          task_id,
          project_task_id,
          organization_id
        `)
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: true });

      // Filter by task_id or project_task_id
      if (projectTaskId) {
        query = query.eq("project_task_id", projectTaskId);
      } else {
        query = query.eq("task_id", taskId);
      }

      const { data: commentsData, error } = await query;

      if (error) {
        console.error("Error fetching comments:", error);
        throw error;
      }

      console.log("Comments fetched:", commentsData);

      if (!commentsData || commentsData.length === 0) {
        return [];
      }

      // Get unique user IDs from comments
      const userIds = [...new Set(
        commentsData
          .filter(comment => comment.created_by)
          .map(comment => comment.created_by)
      )];

      console.log("User IDs to fetch profiles for:", userIds);

      // Fetch user profiles if we have user IDs
      let profiles = [];
      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", userIds);

        if (profilesError) {
          console.error("Error fetching profiles:", profilesError);
        } else {
          profiles = profilesData || [];
          console.log("Profiles fetched:", profiles);
        }
      }

      // Combine comments with profile data
      const commentsWithProfiles = commentsData.map(comment => ({
        ...comment,
        profile: profiles.find(p => p.id === comment.created_by)
      }));

      console.log("Final comments with profiles:", commentsWithProfiles);
      return commentsWithProfiles;
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: async ({ content, guestName }: { content: string; guestName?: string }) => {
      console.log("Adding comment:", { content, guestName, taskId, projectTaskId, organizationId });
      
      // In shared view, always use guest mode
      const sessionData = isSharedView ? null : await supabase.auth.getSession();
      console.log("Current session:", sessionData?.data?.session?.user?.id);
      
      const commentData = {
        content,
        organization_id: organizationId,
        ...(projectTaskId 
          ? { project_task_id: projectTaskId, task_id: taskId }
          : { task_id: taskId }
        ),
        ...(sessionData?.data?.session?.user && !isSharedView
          ? { created_by: sessionData.data.session.user.id }
          : { created_by_guest: guestName || "Usuario Invitado" }
        ),
      };

      console.log("Comment data to insert:", commentData);

      const { data, error } = await supabase
        .from("task_comments")
        .insert([commentData])
        .select()
        .single();

      if (error) {
        console.error("Error inserting comment:", error);
        throw error;
      }

      console.log("Comment inserted successfully:", data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-comments", taskId, projectTaskId] });
      setNewComment("");
      setGuestName("");
      toast({
        title: "Comentario agregado",
        description: "Tu comentario ha sido agregado exitosamente.",
      });
    },
    onError: (error) => {
      console.error("Error adding comment:", error);
      toast({
        title: "Error",
        description: "No se pudo agregar el comentario. Verifica la información.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      // If user is not authenticated (including shared view) and no guest name provided, require guest name
      if (isAuthenticated === false && !guestName.trim()) {
        toast({
          title: "Nombre requerido",
          description: "Por favor ingresa tu nombre para comentar como invitado.",
          variant: "destructive",
        });
        return;
      }
      
      addCommentMutation.mutate({ 
        content: newComment.trim(), 
        guestName: isAuthenticated === false ? guestName.trim() : undefined 
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4" />
        <h3 className="font-medium">Comentarios</h3>
        <span className="text-sm text-muted-foreground">
          ({comments?.length || 0})
        </span>
      </div>

      {/* Comments List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Cargando comentarios...</div>
        ) : comments?.length === 0 ? (
          <div className="text-sm text-muted-foreground">No hay comentarios aún.</div>
        ) : (
          comments?.map((comment) => (
            <Card key={comment.id} className="p-3">
              <CardContent className="p-0">
                <div className="flex gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-xs">
                      {comment.profile?.full_name?.[0] || 
                       comment.profile?.email?.[0] || 
                       comment.created_by_guest?.[0] || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium">
                        {comment.profile?.full_name || 
                         comment.profile?.email || 
                         comment.created_by_guest || "Usuario"}
                      </span>
                      <span className="text-muted-foreground">
                        {formatDistanceToNow(new Date(comment.created_at), { 
                          addSuffix: true, 
                          locale: es 
                        })}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{comment.content}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Comment Form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Guest name input - show if not authenticated (including shared view) */}
        {isAuthenticated === false && (
          <div className="space-y-2">
            <Label htmlFor="guest-name">Tu nombre</Label>
            <Input
              id="guest-name"
              placeholder="Ingresa tu nombre"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              className="text-sm"
            />
          </div>
        )}
        
        <Textarea
          placeholder="Escribir un comentario..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          rows={3}
          className="resize-none"
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={!newComment.trim() || addCommentMutation.isPending || (isAuthenticated === false && !guestName.trim())}
            size="sm"
          >
            <Send className="h-4 w-4 mr-2" />
            {addCommentMutation.isPending ? "Enviando..." : "Comentar"}
          </Button>
        </div>
      </form>
    </div>
  );
};
